import React from 'react';
import { Card, Typography, Steps, Alert, Button, List, Space, Breadcrumb } from 'antd';
import {
  FileTextOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  SafetyOutlined,
  InfoCircleOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import SimpleAdminLayout from '../components/SimpleAdminLayout';

const { Title, Paragraph, Text } = Typography;

function CertificationInfo() {
  const navigate = useNavigate();

  const steps = [
    {
      title: 'Dapatkan Sertifikasi',
      description: 'Ikuti dan selesaikan kursus AI (Google, Coursera, dll)',
      icon: <FileTextOutlined />,
    },
    {
      title: 'Siapkan Link',
      description: 'Simpan link sertifikat (Google Drive, Coursera, dll)',
      icon: <UploadOutlined />,
    },
    {
      title: 'Input URL + Judul',
      description: 'Input link sertifikat dan judul di sistem',
      icon: <CheckCircleOutlined />,
    },
    {
      title: 'Verifikasi Admin',
      description: 'Admin input NIM/NIP + verifikasi → +1 poin leaderboard',
      icon: <SafetyOutlined />,
    },
  ];

  const certificationTypes = [
    {
      name: 'Google AI Essentials',
      description: 'Pelajari dasar-dasar AI dan penerapannya',
      url: 'https://www.cloudskillsboost.google/paths/118',
    },
    {
      name: 'Introduction to Generative AI',
      description: 'Memahami konsep Generative AI',
      url: 'https://www.cloudskillsboost.google/paths/118',
    },
    {
      name: 'Introduction to Large Language Models',
      description: 'Pelajari tentang Large Language Models',
      url: 'https://www.cloudskillsboost.google/paths/118',
    },
    {
      name: 'Machine Learning Crash Course',
      description: 'Kursus intensif Machine Learning',
      url: 'https://developers.google.com/machine-learning/crash-course',
    },
  ];

  const requirements = [
    'URL sertifikat yang valid (https://)',
    'Link harus dapat diakses (Google Drive, Coursera, atau platform lainnya)',
    'Judul sertifikat yang jelas',
    'Sertifikat harus terkait dengan AI/Machine Learning',
  ];

  return (
    <SimpleAdminLayout>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Breadcrumb */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item>Sertifikasi AI</Breadcrumb.Item>
        <Breadcrumb.Item>Panduan Upload</Breadcrumb.Item>
      </Breadcrumb>

      {/* Header */}
      <Card style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <SafetyOutlined /> Panduan Upload Sertifikasi AI (SIMPLE!)
        </Title>
        <Paragraph>
          Upload sertifikasi AI dengan mudah - <strong>cukup URL dan judul saja!</strong> Admin akan
          memverifikasi dan menghubungkan sertifikat ke profil Anda dengan input NIM/NIP.
        </Paragraph>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          💡 <strong>Cara kerja:</strong> Anda input URL + judul → Status pending →
          Admin verifikasi + input NIM/NIP → +1 poin ke leaderboard Anda!
        </Paragraph>
      </Card>

      {/* Alert Info */}
      <Alert
        message="📋 Upload SIMPLE - Hanya URL + Judul!"
        description={
          <div>
            <strong>Yang Diperlukan:</strong> URL sertifikat + Judul saja!<br />
            <strong>TIDAK Perlu:</strong> Upload PDF, pilih jenis, atau input NIM/NIP<br />
            <strong>Scoring:</strong> Setiap sertifikasi verified = <strong>+1 poin</strong> di leaderboard! 🏆
          </div>
        }
        type="info"
        icon={<InfoCircleOutlined />}
        showIcon
        style={{ marginBottom: '24px' }}
      />

      {/* Process Steps */}
      <Card title="Langkah-langkah Upload Sertifikasi" style={{ marginBottom: '24px' }}>
        <Steps
          direction="vertical"
          current={-1}
          items={steps.map(step => ({
            title: step.title,
            description: step.description,
            icon: step.icon,
          }))}
        />
      </Card>

      {/* Available Certifications */}
      <Card
        title="Sertifikasi AI dari Google yang Tersedia"
        style={{ marginBottom: '24px' }}
      >
        <Paragraph>
          Berikut adalah beberapa sertifikasi AI dari Google yang dapat Anda ikuti:
        </Paragraph>
        <List
          dataSource={certificationTypes}
          renderItem={(cert) => (
            <List.Item
              actions={[
                <a
                  href={cert.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button type="link">
                    Kunjungi <span style={{ marginLeft: '4px' }}>→</span>
                  </Button>
                </a>
              ]}
            >
              <List.Item.Meta
                title={<Text strong>{cert.name}</Text>}
                description={cert.description}
              />
            </List.Item>
          )}
        />
      </Card>

      {/* Detailed Instructions */}
      <Card title="Cara Upload Sertifikasi (SIMPLE!)" style={{ marginBottom: '24px' }}>
        <Title level={4}>1. Dapatkan Sertifikasi AI</Title>
        <Paragraph>
          Ikuti kursus AI di platform apapun:
        </Paragraph>
        <ul>
          <li>
            <a
              href="https://www.cloudskillsboost.google"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Cloud Skills Boost
            </a> - Platform utama untuk kursus AI dan Cloud
          </li>
          <li>
            <a
              href="https://www.coursera.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              Coursera
            </a> - Berbagai kursus AI dari universitas ternama
          </li>
          <li>
            <a
              href="https://developers.google.com/machine-learning"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Developers - Machine Learning
            </a> - Kursus ML gratis
          </li>
          <li>Platform AI lainnya (Udacity, edX, dll)</li>
        </ul>

        <Title level={4}>2. Siapkan Link Sertifikat</Title>
        <Paragraph>
          <strong>Opsi 1 - Direct Link:</strong><br />
          - Jika platform menyediakan shareable URL (misal: Coursera), copy URL tersebut<br />
          - Pastikan URL dapat diakses oleh orang lain
        </Paragraph>
        <Paragraph>
          <strong>Opsi 2 - Upload ke Google Drive:</strong><br />
          - Download sertifikat dalam format PDF (jika perlu)<br />
          - Upload ke Google Drive<br />
          - Klik kanan → Get link → Pastikan "Anyone with the link can view"<br />
          - Copy link Google Drive
        </Paragraph>

        <Title level={4}>3. Input URL + Judul di Sistem (HANYA INI!)</Title>
        <Paragraph>
          - Klik tombol &quot;Upload Sertifikasi&quot;<br />
          - Paste URL sertifikat di kolom URL<br />
          - Tulis judul sertifikat (contoh: &quot;Google AI Essentials&quot;)<br />
          - Klik &quot;Simpan&quot; - <strong>SELESAI!</strong>
        </Paragraph>
        <Paragraph type="secondary">
          💡 <strong>Tidak perlu:</strong> Upload file PDF, pilih jenis sertifikat, atau input NIM/NIP.
          Admin yang akan menangani semua itu!
        </Paragraph>

        <Title level={4}>4. Tunggu Verifikasi Admin</Title>
        <Paragraph>
          - Admin akan memverifikasi sertifikat Anda (1-3 hari kerja)<br />
          - Admin akan menginput NIM/NIP untuk menghubungkan ke profil Anda<br />
          - Jika diverifikasi → <strong>+1 poin otomatis</strong> ke leaderboard Anda! 🎉
        </Paragraph>
      </Card>

      {/* Requirements */}
      <Card title="Persyaratan Upload" style={{ marginBottom: '24px' }}>
        <List
          dataSource={requirements}
          renderItem={(req) => (
            <List.Item>
              <CheckCircleOutlined style={{ color: '#14B8A6', marginRight: '8px' }} />
              {req}
            </List.Item>
          )}
        />
      </Card>

      {/* CTA Button */}
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} align="center">
          <Title level={4}>Siap Upload Sertifikasi?</Title>
          <Paragraph>
            Jika Anda sudah memiliki link sertifikat AI, klik tombol di bawah
            untuk menginput URL + judul ke sistem. Sangat mudah!
          </Paragraph>
          <Space>
            <Button
              type="primary"
              size="large"
              icon={<UploadOutlined />}
              onClick={() => navigate('/certifications/upload')}
            >
              Upload Sertifikasi Sekarang
            </Button>
            <Button
              size="large"
              onClick={() => navigate('/certifications')}
            >
              Lihat Sertifikasi Saya
            </Button>
          </Space>
        </Space>
      </Card>

      {/* FAQ */}
      <Card title="Pertanyaan Umum (FAQ)" style={{ marginTop: '24px' }}>
        <Title level={5}>Q: Apakah harus upload file PDF?</Title>
        <Paragraph>
          A: <strong>TIDAK!</strong> Anda hanya perlu input URL link sertifikat dan judul.
          Tidak perlu upload file PDF, pilih jenis, atau input NIM/NIP.
        </Paragraph>

        <Title level={5}>Q: URL dari platform mana saja yang diterima?</Title>
        <Paragraph>
          A: Semua platform sertifikasi AI diterima (Google, Coursera, Udacity, edX, dll).
          Yang penting link dapat diakses dan sertifikat terkait AI/ML.
        </Paragraph>

        <Title level={5}>Q: Bagaimana sertifikat dihubungkan ke profil saya?</Title>
        <Paragraph>
          A: Saat admin memverifikasi sertifikat Anda, admin akan menginput NIM/NIP Anda untuk
          menghubungkan sertifikat ke profil. Sama seperti verifikasi video, ini memastikan
          poin masuk ke leaderboard yang tepat.
        </Paragraph>

        <Title level={5}>Q: Berapa poin yang saya dapat?</Title>
        <Paragraph>
          A: Setiap sertifikasi yang diverifikasi = <strong>+1 poin</strong> di leaderboard.
          Sama seperti video, semua sertifikasi AI dihitung sama (tidak ada multiplier).
        </Paragraph>

        <Title level={5}>Q: Berapa lama proses verifikasi?</Title>
        <Paragraph>
          A: Proses verifikasi biasanya memakan waktu 1-3 hari kerja. Anda dapat melihat
          status di halaman &quot;Sertifikasi Saya&quot;.
        </Paragraph>

        <Title level={5}>Q: Apakah saya bisa upload lebih dari satu sertifikasi?</Title>
        <Paragraph>
          A: Ya! Anda dapat mengupload sebanyak mungkin sertifikasi yang Anda miliki.
          Setiap sertifikasi verified = +1 poin.
        </Paragraph>

        <Title level={5}>Q: Apa yang terjadi jika sertifikasi saya ditolak?</Title>
        <Paragraph>
          A: Jika sertifikasi ditolak, admin akan memberikan catatan/alasan penolakan.
          Anda dapat memperbaiki dan mengupload ulang dengan link yang benar.
        </Paragraph>
      </Card>
      </div>
    </SimpleAdminLayout>
  );
}

export default CertificationInfo;
